import React, { useEffect, useState } from "react";
import {
  Grid,
  Chip,
  Typography,
  useMediaQuery,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Divider,
  Button,
  Skeleton,
  Card,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useNavigate } from "react-router-dom";
import { useCandidateLayout } from "../../Components/Context/CandidateLayoutContext";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import CandidateProfilePage from "../CandidateProfile/CandidateProfilePage";

const CandidateDash = () => {
  const { candidateData, candidateJobData, notifications } =
    useCandidateLayout();

  const isMobile = useMediaQuery("(max-width: 1220px)");

  const Navigate = useNavigate();

  const jobTitle = candidateJobData[0]?.JD_Title;
  const jobId = candidateJobData[0]?.JD_JobId;
  const stages = candidateJobData[0]?.recruitment_json
    ? JSON.parse(candidateJobData[0].recruitment_json)?.stages || []
    : [];

  return (
    <div
      id="candidateProfile"
      style={{
        margin: "auto",
        maxWidth: isMobile ? "100vw" : `calc(100vw - 300px)`,
      }}
    >
      {/* Job Procedure Mapping */}
      <Grid container p={3}>
        <Grid
          item
          xs={12}
          sx={{
            border: "1px solid #006CB880",
            margin: "10px auto",
            borderRadius: "10px",

            padding: "20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "20px",
              gap: 1, // optional spacing between wrapped items
              
            }}
          >
            {/* Application Progress - Job Title */}
            <Typography
              sx={{
                px: 2,
                textAlign: "left",
                fontFamily: "Geist, sans-serif",
                letterSpacing: `0.5px`,
                fontSize: "22px",
                color: "#333",
                fontWeight: "600",
              }}
            >
              ({jobId}) - {jobTitle}
            </Typography>
            <Button
              variant="text"
              onClick={() => Navigate(`/Jobs`)}
              sx={{
                minWidth: "100px",
                color: "white",
                background: "#006CB8",
                mt: 0.5,
                height: "30px",
                textTransform: "capitalize",
                ml: 'auto', // pushes right when wrapped
              }}
            >
              View All
            </Button>
          </Box>

          {/* Horizontal Scrollable Box of Stages */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              padding: 2,
            }}
          >
            {stages.map((stage, stageIndex) => {
              const hasSubProcedures =
                Array.isArray(stage.subProcedures) &&
                stage.subProcedures.length > 0;

              // Check completion per subProcedure
              const subComponents = hasSubProcedures
                ? stage.subProcedures.map((sub) => {
                    const fields = sub?.fields || [];
                    const isSubCompleted = fields.some(
                      (field) =>
                        field?.RPF_Type === "IsCompleted" &&
                        Array.isArray(field?.RPF_Value) &&
                        field?.RPF_Value.includes("1")
                    );

                    const subStatusField = fields.find((field) =>
                      field?.RPF_Name?.trim()?.toLowerCase().includes("status")
                    );

                    return {
                      ...sub,
                      isCompleted: isSubCompleted,
                      statusValue: Array.isArray(subStatusField?.RPF_Value)
                        ? subStatusField.RPF_Value.join(", ")
                        : subStatusField?.RPF_Value || "",
                    };
                  })
                : [];

              // Stage is complete if all subs are complete or own fields say so
              const stageIsCompleted = hasSubProcedures
                ? subComponents.length > 0 &&
                  subComponents.every((s) => s.isCompleted)
                : stage?.fields?.some(
                    (field) =>
                      field?.RPF_Type === "IsCompleted" &&
                      Array.isArray(field?.RPF_Value) &&
                      field?.RPF_Value.includes("1")
                  );

              const stageBorderColor = stageIsCompleted ? "#22C55E" : "#3B82F6";
              const stageBgColor = stageIsCompleted ? "#22C55E1A" : "#3B82F61A";

              // Search all stage + sub fields for first "status" match
              const allFields = [
                ...(stage?.fields || []),
                ...(stage?.subProcedures || []).flatMap(
                  (sub) => sub?.fields || []
                ),
              ];

              const stageStatusField = allFields.find((field) =>
                field?.RPF_Name?.trim()?.toLowerCase().includes("status")
              );

              return (
                <Box
                  key={stageIndex}
                  sx={{
                    minWidth: "200px",
                    width: "auto",
                    border: `2px solid ${stageBorderColor}`,
                    backgroundColor: stageBgColor,
                    borderRadius: 2,
                    p: 2,
                    m: 2,
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
                    display: hasSubProcedures ? "block" : "flex",
                    flexDirection: hasSubProcedures ? "initial" : "column",
                    alignItems: hasSubProcedures ? "initial" : "center",
                    justifyContent: hasSubProcedures ? "initial" : "center",
                    textAlign: hasSubProcedures ? "initial" : "center",
                  }}
                >
                  {/* Main Stage */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      alignContent: "center",
                      justifyContent: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {stageIsCompleted ? (
                      <CheckCircleIcon
                        sx={{ color: "#22C55E", fontSize: 35 }}
                      />
                    ) : (
                      <AccessTimeIcon sx={{ color: "#3B82F6", fontSize: 35 }} />
                    )}
                    <Typography
                      fontWeight={600}
                      fontSize={16}
                      sx={{
                        textTransform: "capitalize",
                        fontFamily: "Geist, sans-serif",
                      }}
                    >
                      {stage?.RPS_Name || "Unnamed Stage"}
                    </Typography>
                    {stageStatusField && (
                      <Typography
                        sx={{
                          color: stageBorderColor,
                          fontFamily: "Geist, sans-serif",
                        }}
                        fontSize={14}
                      >
                        {Array.isArray(stageStatusField.RPF_Value)
                          ? stageStatusField.RPF_Value.join(", ")
                          : stageStatusField.RPF_Value || ""}
                      </Typography>
                    )}
                  </Box>

                  {/* SubTabs (if any) */}
                  {hasSubProcedures &&
                    subComponents.map((sub, subIndex) => {
                      const subColor = sub.isCompleted ? "#22C55E" : "#3B82F6";
                      const subBgColor = sub.isCompleted
                        ? "#22C55E1A"
                        : "#3B82F61A";

                      return (
                        <Box
                          key={subIndex}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            borderLeft: `4px solid ${subColor}`,
                            backgroundColor: subBgColor,
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            fontSize={14}
                            fontWeight={500}
                            sx={{
                              textTransform: "capitalize",
                              fontFamily: "Geist, sans-serif",
                            }}
                          >
                            {sub?.RSP_Name || "Unnamed Sub"}
                          </Typography>
                          {sub.statusValue && (
                            <Typography
                              fontSize={13}
                              sx={{
                                color: subColor,
                                textTransform: "capitalize",
                                fontFamily: "Geist, sans-serif",
                              }}
                            >
                              {sub.statusValue}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                </Box>
              );
            })}

            {/* {stages.map((stage, stageIndex) => (
              <Box
                key={stageIndex}
                sx={{
                  width: `150px`,
                  maxWidth: "150px",
                  height: "150px",
                  flex: "1 0 200px",
                  border: `2px solid grey`,

                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-evenly",
                  padding: 1,
                  marginRight: 2,
                  boxSizing: "border-box",
                }}
              >
                <HourglassBottomIcon sx={{ fontSize: "40px", color: "grey" }} />
                <Typography
                  sx={{
                    textAlign: "center",
                    fontFamily: "Geist, sans-serif",
                    fontSize: "16px",
                    color: "#333",
                    fontWeight: "600",
                  }}
                >
                  {stage?.RPS_Name || "Unnamed Stage"}
                </Typography>
              </Box>
            ))} */}
            {/* 
            {stages?.length > 0 && (
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                lg={4}
                id="statusDetailsSection"
              >
                <Card
                  sx={{
                    height: "69%",
                    border: "1px solid lightgrey",
                    boxShadow: "none",
                    marginBottom: "4%",
                    overflowY: "auto",
                  }}
                >
                  <Box sx={{ padding: "15px" }}>
                    {stages?.map((stage, index) => {
                      const hasSubProcedures =
                        Array.isArray(stage.subProcedures) &&
                        stage.subProcedures.length > 0;

                      let stageIsCompleted = false;

                      const subComponents = hasSubProcedures
                        ? stage.subProcedures.map((sub) => {
                            const fields = sub?.fields || [];

                            const isSubCompleted = fields.some(
                              (field) =>
                                field?.RPF_Type === "IsCompleted" &&
                                Array.isArray(field?.RPF_Value) &&
                                field?.RPF_Value.includes("1")
                            );

                            return {
                              ...sub,
                              isCompleted: isSubCompleted,
                            };
                          })
                        : [];

                      if (hasSubProcedures) {
                        stageIsCompleted =
                          subComponents.length > 0 &&
                          subComponents.every((s) => s.isCompleted);
                      } else {
                        stageIsCompleted = stage?.fields?.some(
                          (field) =>
                            field?.RPF_Type === "IsCompleted" &&
                            Array.isArray(field?.RPF_Value) &&
                            field?.RPF_Value.includes("1")
                        );
                      }

                      const stageBorderColor = stageIsCompleted
                        ? "#22C55E"
                        : "#3B82F6";
                      const stageBgColor = stageIsCompleted
                        ? "#22C55E0D"
                        : "#3B82F60D";

                      // Search all stage + sub fields for first "status" match
                      const allFields = [
                        ...(stage?.fields || []),
                        ...(stage?.subProcedures || []).flatMap(
                          (sub) => sub?.fields || []
                        ),
                      ];

                      const stageStatusField = allFields.find((field) =>
                        field?.RPF_Name?.trim()
                          ?.toLowerCase()
                          .includes("status")
                      );

                      return (
                        <Box
                          key={index}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            backgroundColor: stageBgColor,
                            borderLeft: `5px solid ${stageBorderColor}`,
                            boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            {stageIsCompleted ? (
                              <CheckCircleIcon sx={{ color:"green", fontSize: 24 }} />
                            ) : (
                              <AccessTimeIcon sx={{ color:'blue', fontSize: 24 }} />
                            )}
                            <Box>
                              <Typography fontWeight="bold">
                                {stage.RPS_Name}
                              </Typography>
                              {stageStatusField && (
                                <Typography
                                  sx={{ color: stageBorderColor }}
                                  fontSize={14}
                                >
                                  {Array.isArray(stageStatusField.RPF_Value)
                                    ? stageStatusField.RPF_Value.join(", ")
                                    : stageStatusField.RPF_Value || ""}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {hasSubProcedures &&
                            subComponents.map((sub, subIndex) => {
                              const subBorderColor = sub.isCompleted
                                ? "#22C55E"
                                : "#3B82F6";
                              const subBgColor = sub.isCompleted
                                ? "#22C55E1A"
                                : "#3B82F61A";

                              const subStatusField = (sub?.fields || []).find(
                                (field) =>
                                  field?.RPF_Name?.trim()
                                    ?.toLowerCase()
                                    .includes("status")
                              );

                              return (
                                <Box
                                  key={`sub-${subIndex}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mt: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: subBgColor,
                                    borderLeft: `4px solid ${subBorderColor}`,
                                  }}
                                >
                                
                                  <Box>
                                    <Typography fontWeight={500}>
                                      {sub.RSP_Name}
                                    </Typography>
                                    {subStatusField && (
                                      <Typography
                                        sx={{ color: subBorderColor }}
                                        fontSize={13}
                                      >
                                        {Array.isArray(subStatusField.RPF_Value)
                                          ? subStatusField.RPF_Value.join(", ")
                                          : subStatusField.RPF_Value || ""}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              </Grid>
            )} */}
          </Box>
        </Grid>
      </Grid>

      <CandidateProfilePage isDash={true} />

      <Grid container px={3} spacing={3}>
        {/* Notifications */}
        {notifications?.length > 0 && (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Box
              sx={{
                border: "1px solid #006CB880",
                borderRadius: "10px",
                minHeight: "200px",
                padding: "20px",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: "20px",
                  gap: 1, // optional spacing between wrapped items
                }}
              >
                <Typography
                  sx={{
                    px: 2,
                    textAlign: "left",
                    mt: 0.5,
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "0.5px",
                    fontSize: "30px",
                    color: "#333",
                    fontWeight: 600,
                    flexGrow: 1,
                  }}
                >
                  Notifications
                </Typography>

                <Button
                  variant="text"
                  onClick={() => Navigate(`/Notifications`)}
                  sx={{
                    minWidth: "100px",
                    color: "white",
                    background: "#006CB8",
                    mt: 0.5,
                    height: "30px",
                    textTransform: "capitalize",
                    ml: { xs: "auto", sm: 0 }, // pushes right when wrapped
                  }}
                >
                  View All
                </Button>
              </Box>

              <Box pl={2}>
                {notifications.slice(0, 4).map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      top: "5px",
                      minHeight: { sm: "", md: "70px" },
                      // border: "2px solid red",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent={"flex-start"}
                      alignContent={"flex-start"}
                      spacing={2}
                    >
                      <Box display="flex" alignContent={"flex-start"} gap={1}>
                        <CircleIcon
                          color="info"
                          sx={{
                            width: 15,
                            color: "#006CB8",
                            position: "relative",
                            top: "2px",
                          }}
                        />
                        <Typography
                          sx={{
                            textAlign: "left",
                            fontFamily: "Geist, sans-serif",
                            letterSpacing: `0.5px`,
                            fontSize: "18px",
                            color: "#333",
                            fontWeight: "400",
                          }}
                        >
                          {item.NM_Message}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        )}

        {/* Job Summary */}
        {candidateJobData?.length > 0 && (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Box
              sx={{
                border: "1px solid #006CB880",
                borderRadius: "10px",
                minHeight: "200px",
                padding: "20px",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: "20px",
                  gap: 1, // optional spacing between items
                }}
              >
                <Typography
                  sx={{
                    px: 2,
                    textAlign: "left",
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "0.5px",
                    fontSize: "30px",
                    color: "#333",
                    fontWeight: 600,
                    flexGrow: 1, // take as much space as needed
                  }}
                >
                  Job Summary
                </Typography>

                <Button
                  variant="text"
                  onClick={() => Navigate(`/Summary`)}
                  sx={{
                    minWidth: "100px",
                    color: "white",
                    background: "#006CB8",
                    mt: 0.5,
                    height: "30px",
                    textTransform: "capitalize",
                    ml: { xs: "auto", sm: 0 }, // push to right on wrap
                  }}
                >
                  View All
                </Button>
              </Box>

              <Box pl={2}>
                {candidateJobData.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      top: "5px",
                      minHeight: { sm: "", md: "70px" },
                      padding: "6px 0px",
                    }}
                  >
                    {/* Bullet marker */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: "10px",
                        width: 12,
                        height: 12,
                        bgcolor: "#006CB8",
                        borderRadius: "50%",
                        zIndex: 1,
                      }}
                    />

                    {/* Content */}
                    <Grid
                      container
                      spacing={1}
                      alignItems="flex-start"
                      sx={{
                        position: "relative",
                        bottom: "5px",
                        pl: "20px",
                        // border: {
                        //   xs: "2px solid yellow",
                        //   sm: "2px solid red",
                        //   md: "2px solid blue",
                        //   lg: "2px solid green",
                        //   xl: "2px solid violet",
                        // },
                      }}
                    >
                      {/* Title + JobId */}
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={8}>
                        <Typography
                          sx={{
                            textAlign: "left",
                            fontFamily: "Geist, sans-serif",
                            letterSpacing: `0.5px`,
                            fontSize: "18px",
                            color: "#333",
                            fontWeight: "600",
                          }}
                        >
                          {item?.JD_Title}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "left",
                            fontFamily: "Geist, sans-serif",
                            letterSpacing: `0.5px`,
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: "400",
                          }}
                        >
                          {item?.JD_JobId}
                        </Typography>
                      </Grid>

                      {/* Date + Status */}
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={4}
                        textAlign={{
                          xs: "left",
                          sm: "left",
                          md: "left",
                          lg: "left",
                          xl: "right",
                        }}
                        sx={{ pr: { sm: 2 } }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Geist, sans-serif",
                            letterSpacing: `0.5px`,
                            fontSize: "16px",
                            color: "#333",
                            fontWeight: "600",
                          }}
                        >
                          {item?.JA_AppliedDate}
                        </Typography>
                        <Chip
                          label={item?.JASL_Name}
                          sx={{
                            minWidth: "100px",
                            color: `white`,
                            backgroundColor: `#006CB8`,
                            mt: 0.5,
                          }}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default CandidateDash;
